import { logo } from 'ui/assets/images';
const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;

export type IBreadcrumbs = { name: string; item?: string }[];

const breadcrumb = (data: IBreadcrumbs = []) => {
  if (!data.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: data.map((val, index) => {
      const obj: {
        '@type': string;
        position: number;
        name: string;
        item?: string;
      } = {
        '@type': 'ListItem',
        position: index + 1,
        name: val?.name,
      };

      if (val?.item) {
        obj.item = val?.item;
      }

      return obj;
    }),
  };
};

export type IFaqs = { question: string; answer: string }[];

const faq = (data: IFaqs = []) => {
  if (!data.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.map((faq) => ({
      '@type': 'Question',
      name: faq?.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq?.answer,
      },
    })),
  };
};

export interface IArticle {
  titlePage?: string;
  heroImageLink?: string;
  publishedDate?: string;
}

const newsArticle = (data: IArticle) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: data?.titlePage,
    image: [data?.heroImageLink],
    datePublished: data?.publishedDate,
    dateModified: data?.publishedDate,
    author: [
      {
        '@type': 'Organization',
        name: 'Auto2000',
        url: PUBLIC_URL,
      },
    ],
  };
};

const reviewArticle = (data: IArticle) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: data?.titlePage,
    image: [data?.heroImageLink],
    datePublished: data?.publishedDate,
    dateModified: data?.publishedDate,
    author: [
      {
        '@type': 'Organization',
        name: 'Auto2000',
        url: PUBLIC_URL,
      },
    ],
  };
};

export type ICustomerReview = {
  reviewerName?: string;
  objectCode?: string;
  rating?: number;
  createdDate?: string;
  message?: string;
}[];

const customerReview = (data: ICustomerReview) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: data[0].reviewerName,
    },
    itemReviewed: {
      '@type': 'LocalBusiness',
      name: data[0].objectCode,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: data[0].rating,
      bestRating: 5,
      worstRating: 1,
    },
    datePublished: data[0].createdDate,
    publisher: {
      '@type': 'Organization',
      name: 'Auto2000',
      url: PUBLIC_URL,
      logo: `${PUBLIC_URL}${logo.src}`,
    },
    reviewBody: data[0].message,
  };
};

const organization = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: PUBLIC_URL,
    logo: `${PUBLIC_URL}${logo.src}`,
  };
};

export type IProduct = {
  product?: {
    code?: string;
    name?: string;
    imageUrl?: string;
    description?: string;
    url?: string;
  };
  detailCar?: {
    lowestPriceVariant?: {
      code?: string;
      name?: string;
      price?: {
        value?: number;
      };
      isPreorder?: boolean;
    };
    strikeThroughPrice?: {
      value?: number;
    };
    variantOptions?: {
      variants?: {
        code?: string;
        name?: string;
        price?: {
          value?: number;
        };
        isPreorder?: boolean;
      }[];
    }[];
  };
  rateData?: {
    content?: {
      rating?: number;
      reviewerName?: string;
      createdDate?: string;
      message?: string;
    }[];
  };
};

const product = (data: IProduct) => {
  const selectedCar = data?.detailCar?.variantOptions
    ?.find((d) =>
      d?.variants?.find((variant) => variant?.code === data?.detailCar?.lowestPriceVariant?.code)
    )
    ?.variants?.find((variant) => variant?.code === data?.detailCar?.lowestPriceVariant?.code);

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: data?.product?.code,
    model: selectedCar?.name,
    image: data?.product?.imageUrl,
    description: data?.product?.description,
    brand: {
      '@type': 'Brand',
      name: 'Toyota',
    },
    sku: data?.product?.code,
    offers: {
      '@type': 'Offer',
      url: `${PUBLIC_URL}${data?.product?.url}`,
      priceCurrency: 'IDR',
      price: data?.detailCar?.strikeThroughPrice?.value || selectedCar?.price?.value,
      availability: selectedCar?.isPreorder
        ? 'https://schema.org/PreOrder'
        : 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      bestRating: '5',
      worstRating: '1',
      ratingCount: data?.rateData?.content?.length,
      reviewCount: data?.rateData?.content?.length,
    },
    review: data?.rateData?.content?.map((item) => ({
      '@type': 'Review',
      name: item?.reviewerName,
      reviewBody: item?.message,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: item?.rating,
        bestRating: '5',
        worstRating: '1',
      },
      datePublished: item?.createdDate,
      author: {
        '@type': 'Person',
        name: 'Auto2000 Digiroom',
      },
    })),
  };
};

export type ICar = {
  name?: string;
  image?: string;
  url?: string;
  price?: number;
  color?: string;
  model?: string;
  fuelType?: string;
  vehicleConfiguration?: string;
  vehicleTransmission?: string;
  numberOfDoors?: number;
  vehicleSeatingCapacity?: number;
  description?: string;
};

const car = (data: ICar) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Car',
    name: data?.name || null,
    image: data?.image || null,
    url: data?.url || null,
    model: data?.model || null,
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      price: data?.price || null,
      priceCurrency: 'IDR',
    },
    itemCondition: 'https://schema.org/NewCondition',
    brand: {
      '@type': 'Brand',
      name: 'Toyota',
    },
    color: data?.color || null,
    fuelType: data?.fuelType || null,
    vehicleConfiguration: data?.vehicleConfiguration || null,
    vehicleTransmission: data?.vehicleTransmission || null,
    numberOfDoors: data?.numberOfDoors || null,
    vehicleSeatingCapacity: data?.vehicleSeatingCapacity || null,
    description: data?.description || null,
  };
};

export type IVideo = {
  title?: string;
  description?: string;
  url?: string;
  thumbnailUrl?: string;
  uploadDate?: string;
};

const videoSchema = (data: IVideo) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: data?.title,
    description: data?.description,
    thumbnailUrl: data?.url,
    uploadDate: null,
  };
};

export type IBannerImg = {
  imageUrl?: string;
};

const bannerImg = (data: IBannerImg) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    URL: data.imageUrl,
  };
};

export type IItemList = {
  url: string;
  items: {
    price: number;
    priceCurrency: string;
    name: string;
    image: string;
    urlPdp: string;
    accessoriesCategory?: string;
  }[];
};

const itemList = (data: IItemList) => {
  if (!data.items.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url: data.url,
    numberOfItems: data.items.length,
    itemListElement: data.items.map((val, index) => {
      const obj: {
        '@type': string;
        position: number;
        item: {
          '@type': string;
          image: string;
          url: string;
          name: string;
          offers: {
            '@type': string;
            price: number;
            priceCurrency: string;
            url: string;
          };
        };
      } = {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          image: val.image,
          url: val.urlPdp,
          name: val.name,
          offers: {
            '@type': 'Offer',
            price: val.price,
            priceCurrency: val.priceCurrency,
            url: val.urlPdp,
          },
        },
      };
      return obj;
    }),
  };
};

export type IProductAcc = {
  name: string;
  description: string;
  image: string;
  brand: string;
  price: string;
  priceCurrency: string;
};

const productAccessories = (data: IProductAcc) => {
  const { name, image, description, brand, price, priceCurrency } = data || {};
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: name,
    image: image,
    description: description,
    brand: {
      '@type': 'Brand',
      name: brand,
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: priceCurrency,
      price: price,
      availability: 'https://schema.org/InStock',
    },
  };
};

export type IDetailUsedCar = {
  name: string;
  brand: string;
  model: string;
  vehicleModelDate: string;
  color: string;
  mileageFromOdometer: string;
  url: string;
  price: string;
};

const detailUsedCar = (data: IDetailUsedCar) => {
  const { name, brand, model, vehicleModelDate, color, mileageFromOdometer, url, price } =
    data || {};
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: name,
    brand: {
      '@type': 'Brand',
      name: brand,
    },
    model: model,
    vehicleModelDate: vehicleModelDate,
    color: color,
    mileageFromOdometer: {
      '@type': 'QuantitativeValue',
      value: mileageFromOdometer,
      unitCode: 'KM',
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'IDR',
      price: price,
      availability: 'https://schema.org/InStock',
      url: url,
    },
  };
};

const website = () => {
  return {
    '@context': 'https://schema.org/',
    '@type': 'WebSite',
    name: 'Auto2000',
    url: PUBLIC_URL,
  };
};

const corporate = () => {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Organization',
    name: 'Auto2000',
    url: PUBLIC_URL,
    logo: `${PUBLIC_URL}${logo.src}`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '082289802000',
      contactType: 'customer service',
      contactOption: 'TollFree',
      areaServed: 'ID',
      availableLanguage: 'Indonesian',
    },
    sameAs: [
      'https://www.facebook.com/Auto2000official',
      'https://www.twitter.com/Auto2000ID',
      'https://www.instagram.com/auto2000id/',
      'https://www.youtube.com/user/auto2000csr',
    ],
  };
};

export interface IWebPageData {
  name: string;
  description: string;
  url: string;
  keywords: string;
}

const webpage = (data: IWebPageData) => {
  return {
    '@context': 'https://schema.org/',
    '@type': 'WebPage',
    name: data.name || '',
    description: data.description || '',
    url: data.url || '',
    keywords: data.keywords || '',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Auto2000 Official Website',
      url: PUBLIC_URL,
    },
  };
};

const generateStructuredData = {
  breadcrumb,
  faq,
  organization,
  newsArticle,
  reviewArticle,
  customerReview,
  product,
  car,
  videoSchema,
  bannerImg,
  itemList,
  productAccessories,
  website,
  corporate,
  detailUsedCar,
  webpage,
};

export default generateStructuredData;
